/**
 * Layer-preserving runtime theme application for the Meridian map.
 *
 * # Why not ``map.setStyle()``
 *
 * The obvious way to flip the basemap theme is to rebuild the style
 * with :func:`buildMapStyle` and call ``map.setStyle(next)``. We do
 * **not** do that. The Map view adds its data layers — snapshot,
 * cluster, trail, track-tail, event — *imperatively* on the map's
 * ``'load'`` event (see :file:`Map.tsx` and the ``*-layer-impl.ts``
 * modules). A full ``setStyle`` swaps the entire style document and
 * re-fires ``'load'`` only on the *new* style; MapLibre tears down
 * the old style's sources/layers in the process. The data layers
 * are added through React effects keyed on the *loaded map
 * instance*, not on the style — so they would be wiped by the
 * swap and **not re-added**, and every marker / cluster / trail /
 * event glyph would vanish on a theme toggle.
 *
 * Instead, :func:`applyMapTheme` performs a **targeted** update of
 * only the basemap surface, leaving every data source/layer
 * untouched:
 *
 *   - ``background`` layer ``background-color`` → the theme's map
 *     background (:const:`buildMapStyle`'s ``LIGHT_MAP_BG`` /
 *     ``DARK_MAP_BG``).
 *   - **Production dark** (a ``darkLayer`` is configured): point the
 *     ``meridian-base`` raster source at the dark tile URL via
 *     ``setTiles`` (REQ-UI-045); clear any dev darkening filter.
 *   - **Dev dark** (no ``darkLayer``): keep the light (daytime) tile
 *     URL and apply :const:`DARK_RASTER_FILTER` to the raster layer
 *     via ``setPaintProperty`` so the daytime imagery reads dark.
 *   - **Light**: reset the raster source to the light tile URL and
 *     reset the raster paint to :const:`LIGHT_RASTER_FILTER` (spec
 *     defaults) so a dark→light flip fully clears the darkening.
 *
 * This is the locked decision for this PR: theme flip = targeted
 * source/paint swap, never ``setStyle``.
 *
 * # Basemap **kind** changes — :func:`rebuildBasemap`
 *
 * :func:`applyMapTheme` re-points or re-paints the *existing* basemap; it
 * cannot turn a raster source into a vector one (or back). When the
 * resolved descriptor's **kind** changes (raster ↔ vector) — the initial
 * fail-safe NRL raster giving way to the late-resolved OpenFreeMap vector
 * default, or a Profile picker switch across kinds (REQ-UI-091) — or a
 * vector source's TileJSON ``url`` changes, :func:`basemapRebuildNeeded`
 * flags it and :func:`rebuildBasemap` swaps the basemap **sub-graph**
 * (remove the old basemap layers + source; add the new source + layers
 * *below the first overlay layer*). This still honours the never-
 * ``setStyle`` invariant: the overlay data layers are untouched, so they
 * survive the kind change exactly as they survive a theme flip.
 *
 * Cross-references:
 *   - REQ-UI-040 (paired light/dark themes).
 *   - REQ-UI-041 (system theme reactive — the caller resolves
 *     ``system`` and re-invokes this with the resolved value).
 *   - REQ-UI-045 (theme-aware tile style — the descriptor's
 *     ``dark_layer`` / ``darkUrlTemplate``, browser-direct or via the
 *     retained BFF proxy).
 *   - REQ-UI-046 (status-colour legibility preserved on the dark
 *     basemap — the data layers survive the flip unchanged).
 *   - ADR-016 §4 (WebGL rendering) + 2026-05-12 amendments.
 *   - docs/engineering/detail/storyboards/flows/02-map-view/README.md.
 *   - docs/engineering/detail/storyboards/flows/P-ui-preferences/README.md.
 */
import type {
  StyleSpecification,
  LayerSpecification,
  Map as MapLibreMap,
  RasterTileSource,
} from "maplibre-gl";
import type { ResolvedTheme } from "@/theme/tokens";
import { isMapUsable } from "@/map/map-ready";
import { THEME_TOKENS } from "@/theme/tokens";
import {
  buildMapStyleFromSpec,
  composeTileUrlFromTemplate,
  DARK_RASTER_FILTER,
  DEFAULT_TILE_FORMAT,
  DEFAULT_TILE_LAYER,
  legacyProxyUrlTemplate,
  LIGHT_RASTER_FILTER,
  MAP_BACKGROUND_LAYER_ID,
  MAP_BASE_RASTER_LAYER_ID,
  MAP_BASE_SOURCE_ID,
  type TileSourceSpec,
} from "@/map/style";
import {
  VECTOR_BASEMAP_LAYER_IDS,
  VECTOR_BASEMAP_SOURCE_ID,
  vectorLayerPaints,
} from "@/map/style-vector";

/**
 * The paint-property name accepted by ``Map.setPaintProperty``.
 *
 * Derived from the method signature rather than imported by name: maplibre-gl 5
 * introduced this constraint (it was ``string`` in 4.x) as ``keyof
 * AllPaintProperties``, and deriving it keeps this file compiling if that type
 * is renamed or stops being exported.
 */
type PaintPropertyName = Parameters<MapLibreMap["setPaintProperty"]>[1];

/**
 * The paint-property value accepted by ``Map.setPaintProperty``.
 *
 * Constrained alongside the name in maplibre-gl 5 — it is the style-spec value
 * union, not ``any``. Derived for the same reason as :type:`PaintPropertyName`.
 */
type PaintPropertyValue = Parameters<MapLibreMap["setPaintProperty"]>[2];

/**
 * Options for :func:`applyMapTheme`. Mirror the tile params of
 * :func:`buildMapStyle` so the runtime flip composes the *same* tile
 * URL the initial style would have — for both the legacy
 * ``tileBaseUrl`` form and the descriptor-driven ``spec`` form
 * (ADR-016 amended 2026-06-10).
 *
 * When ``spec`` is supplied it is authoritative — the legacy
 * ``layer`` / ``format`` / ``darkLayer`` are ignored (just as in
 * :func:`buildMapStyle`).
 */
export interface ApplyMapThemeOptions {
  /**
   * Legacy BFF tile-proxy base URL (e.g. ``"/api/tiles"``). Ignored
   * when ``spec`` is supplied; the factory synthesises the relative
   * proxy template from it otherwise.
   */
  tileBaseUrl: string;
  /**
   * Light/default tile layer name (legacy form). Defaults to
   * :const:`DEFAULT_TILE_LAYER`. Ignored when ``spec`` is supplied.
   */
  layer?: string;
  /** Tile format (legacy form). Defaults to :const:`DEFAULT_TILE_FORMAT`. */
  format?: string;
  /**
   * Optional production dark tile layer (legacy form). When supplied,
   * the dark theme requests this layer via ``setTiles`` instead of
   * darkening the light tiles in-shader (REQ-UI-045). Ignored when
   * ``spec`` is supplied.
   */
  darkLayer?: string;
  /**
   * Descriptor-derived tile source (config-driven path; ADR-016
   * amended 2026-06-10). When present it overrides the legacy params
   * above — the runtime flip points the basemap source at the
   * descriptor's (possibly absolute) ``urlTemplate`` /
   * ``darkUrlTemplate``, keeping the initial style + the flip in
   * lock-step.
   */
  spec?: TileSourceSpec | null;
}

/**
 * Compose a concrete ``{z}/{x}/{y}`` tile URL from the resolved
 * options for a given layer — identical substitution to
 * :func:`buildMapStyle` (via :func:`composeTileUrlFromTemplate`) so
 * the runtime flip and the initial style agree byte-for-byte.
 *
 * The ``url_template`` is the descriptor's ``spec.urlTemplate`` when a
 * spec is supplied (absolute ⇒ browser-direct), else the synthesised
 * legacy relative proxy template from ``options.tileBaseUrl``.
 */
function composeTileUrl(options: ApplyMapThemeOptions, layer: string, format: string): string {
  const template = options.spec?.urlTemplate ?? legacyProxyUrlTemplate(options.tileBaseUrl);
  return composeTileUrlFromTemplate(template, layer, format);
}

/**
 * Apply ``resolvedTheme`` to a live **vector** basemap by re-painting
 * each basemap layer in place — the vector analogue of the raster
 * source/paint flip, and equally **never** ``setStyle``.
 *
 * A vector basemap has no raster source to ``setTiles`` and no in-shader
 * darkening filter; instead, light and dark are two different sets of
 * layer ``paint`` values over the *same* layer membership
 * (:func:`buildVectorBasemap`). So the flip walks
 * :func:`vectorLayerPaints` for the target theme and calls
 * ``setPaintProperty`` for every (layer, paint-prop) pair — but only for
 * layers that are actually present (so a half-loaded map, or a vector
 * style that hasn't replaced a prior raster style yet, no-ops safely;
 * CLAUDE.md §8.1). The overlay data layers are never named here (their
 * ids aren't in the vector basemap set), so they survive untouched —
 * the headline REQ-UI-040 regression guard holds for vector too.
 *
 * @param map - The loaded MapLibre map instance (already
 *     :func:`isMapUsable`-guarded by the caller).
 * @param resolvedTheme - ``"light"`` or ``"dark"``.
 * @param tileJsonUrl - The vector source's TileJSON url (passed to the
 *     paint builder; the paints don't depend on it, but the builder
 *     signature requires it).
 *
 * Cross-references:
 *   - REQ-UI-040 / REQ-UI-045 (paired themes; layer-preserving flip).
 *   - REQ-UI-046 (overlay layers untouched → status contrast holds).
 *   - ADR-016 §4 + 2026-06-10 amendment.
 */
function applyVectorMapTheme(
  map: MapLibreMap,
  resolvedTheme: ResolvedTheme,
  tileJsonUrl: string,
): void {
  const paints = vectorLayerPaints(resolvedTheme, tileJsonUrl);
  for (const [layerId, paint] of Object.entries(paints)) {
    // Skip a layer the live map doesn't carry (half-loaded, or a vector
    // style not yet applied over a prior raster style) — never throw.
    if (map.getLayer(layerId) === undefined) {
      continue;
    }
    for (const [prop, value] of Object.entries(paint)) {
      // ``Object.entries`` widens the key to ``string``, but maplibre-gl 5+
      // types ``setPaintProperty``'s second parameter as the union of known
      // paint-property names. The narrowing is sound here — the keys come from
      // :func:`vectorLayerPaints`, which only ever emits real paint properties
      // — and it is expressed through ``Parameters<>`` rather than by naming
      // ``keyof AllPaintProperties`` so it survives maplibre renaming an
      // internal type.
      map.setPaintProperty(layerId, prop as PaintPropertyName, value as PaintPropertyValue);
    }
  }
}

/**
 * Apply ``resolvedTheme`` to a live, loaded MapLibre map by
 * targeted source/paint updates — never ``setStyle``. Idempotent
 * and safe to call repeatedly (e.g. on every render that re-runs
 * the driving effect).
 *
 * No-ops gracefully if the basemap source / background layer /
 * raster layer aren't present yet (the map hasn't finished loading,
 * or a test mock omits them). Operator-visibility (CLAUDE.md §8.1):
 * the function never throws on a missing basemap surface — a
 * half-built map simply isn't re-themed until its surfaces exist.
 *
 * @param map - The loaded MapLibre map instance.
 * @param resolvedTheme - ``"light"`` or ``"dark"`` (the caller
 *     resolves ``"system"`` first per REQ-UI-041).
 * @param options - Tile params mirroring :func:`buildMapStyle`:
 *     either the legacy ``tileBaseUrl`` form or the descriptor-driven
 *     ``spec`` (config-driven; ADR-016 amended 2026-06-10). When
 *     ``spec.type === "vector"`` the flip delegates to
 *     :func:`applyVectorMapTheme` — a per-layer paint re-apply over the
 *     vector basemap layers — instead of the raster source/filter path.
 *
 * Cross-references:
 *   - REQ-UI-033 / REQ-UI-034 (browser-direct + retained proxy).
 *   - REQ-UI-040 / REQ-UI-045 / REQ-UI-046.
 *   - ADR-016 §4 + 2026-06-10 amendment.
 */
export function applyMapTheme(
  map: MapLibreMap,
  resolvedTheme: ResolvedTheme,
  options: ApplyMapThemeOptions,
): void {
  // No-op safely on a removed / not-ready map. Driven from a Map.tsx effect
  // keyed on the loaded map + theme; a theme flip racing the "Reset to profile"
  // panel rebuild could otherwise run ``getLayer`` / ``getSource`` against a
  // ``map.remove()``-d map and throw (see :file:`map/map-ready.ts`).
  if (!isMapUsable(map)) {
    return;
  }
  const isDark = resolvedTheme === "dark";
  const spec = options.spec;

  // ── No basemap (#1002) ───────────────────────────────────────────
  // The once-at-mount style while ``/api/config`` is unresolved: only
  // the background carries theme. There is no source to re-point and
  // nothing to build here — ``rebuildBasemap`` adds the basemap when the
  // descriptor lands. Keep the background theme-correct and return.
  if (spec === null) {
    if (map.getLayer(MAP_BACKGROUND_LAYER_ID) !== undefined) {
      map.setPaintProperty(
        MAP_BACKGROUND_LAYER_ID,
        "background-color",
        isDark ? THEME_TOKENS.dark.map.bgFrom : THEME_TOKENS.light.map.bgFrom,
      );
    }
    return;
  }

  // ── Vector branch (ADR-016 amended 2026-06-10) ───────────────────
  // A vector basemap carries BOTH themes' palettes in the layer paint
  // (not a raster source swap + in-shader darkening). The flip is
  // therefore a per-layer ``setPaintProperty`` over exactly the vector
  // basemap layers — NEVER ``setStyle`` (which would wipe the
  // imperatively-added overlay layers, the same headline regression the
  // raster path guards). The target-theme paint comes from
  // :func:`vectorLayerPaints`, the same builder that produced the
  // initial style, so the flip can't drift from first paint. Overlay
  // layers (snapshot / cluster / detection / alert / geometry) are never
  // touched — their ids aren't in :const:`VECTOR_BASEMAP_LAYER_IDS`.
  if (spec?.type === "vector") {
    applyVectorMapTheme(map, resolvedTheme, spec.urlTemplate);
    return;
  }

  // Resolve effective tile params: the descriptor ``spec`` is
  // authoritative when present; otherwise the legacy ``layer`` /
  // ``format`` / ``darkLayer``. ``hasDarkBasemap`` is whether a real
  // dark basemap exists (vs. in-shader darkening): for a spec it's a
  // non-null ``darkUrlTemplate``; for the legacy form it's a passed
  // ``darkLayer``.
  const layer = spec?.layer ?? options.layer ?? DEFAULT_TILE_LAYER;
  const format = spec?.format ?? options.format ?? DEFAULT_TILE_FORMAT;
  // The named layer to substitute into the dark template's ``{layer}``:
  // the spec's ``darkLayer`` (descriptor ``dark_layer``) when set, the
  // legacy ``darkLayer`` otherwise, falling back to the light layer.
  const darkLayerName = spec ? (spec.darkLayer ?? layer) : (options.darkLayer ?? layer);
  const hasDarkBasemap = spec ? spec.darkUrlTemplate != null : options.darkLayer != null;

  // 1) Background layer color — flips the pre-tile / out-of-coverage
  //    flash to match the theme. Guard on layer presence so a
  //    not-yet-loaded map (or a minimal test mock) doesn't throw.
  if (map.getLayer(MAP_BACKGROUND_LAYER_ID) !== undefined) {
    map.setPaintProperty(
      MAP_BACKGROUND_LAYER_ID,
      "background-color",
      isDark ? THEME_TOKENS.dark.map.bgFrom : THEME_TOKENS.light.map.bgFrom,
    );
  }

  // 2) Basemap raster source tiles. Three cases for whether/what to
  //    ``setTiles``:
  //
  //    a. Dark + a real dark basemap → the dark template + dark layer.
  //    b. Otherwise, when a descriptor ``spec`` is present OR a legacy
  //       dark basemap exists → the light template + light layer. The
  //       spec branch makes the descriptor authoritative: it re-points
  //       the source if the resolved descriptor arrives / changes after
  //       the initial style was built (config is async; a late or
  //       Profile-switched descriptor must re-point, not silently keep
  //       stale tiles — CLAUDE.md §8.1), and it covers a dark→light
  //       return in production.
  //    c. Pure legacy dev path (no spec, no dark basemap) → leave tiles
  //       untouched so a theme toggle never triggers a tile re-fetch /
  //       flicker (the daytime tiles are darkened in-shader in step 3).
  const source = map.getSource(MAP_BASE_SOURCE_ID) as RasterTileSource | undefined;
  if (source !== undefined && typeof source.setTiles === "function") {
    if (isDark && hasDarkBasemap) {
      // Dark template (spec.darkUrlTemplate) substituted with the dark
      // layer; for the legacy form the dark template equals the light
      // (proxy) one with ``darkLayer`` swapped in.
      const darkTemplate = spec?.darkUrlTemplate ?? legacyProxyUrlTemplate(options.tileBaseUrl);
      source.setTiles([composeTileUrlFromTemplate(darkTemplate, darkLayerName, format)]);
    } else if (spec !== undefined || hasDarkBasemap) {
      // Light tiles — descriptor-authoritative re-point, or a legacy
      // dark→light return. (The pure legacy dev path falls through and
      // leaves tiles untouched.)
      source.setTiles([composeTileUrl(options, layer, format)]);
    }
  }

  // 3) Raster darkening filter — applied only in the in-shader dark
  //    path (dark theme, no real dark basemap). Reset to spec defaults
  //    for light, and also for production dark (the dark tiles are
  //    already dark, so no in-shader darkening is wanted).
  if (map.getLayer(MAP_BASE_RASTER_LAYER_ID) !== undefined) {
    const useDevDarkFilter = isDark && !hasDarkBasemap;
    const filter = useDevDarkFilter ? DARK_RASTER_FILTER : LIGHT_RASTER_FILTER;
    map.setPaintProperty(
      MAP_BASE_RASTER_LAYER_ID,
      "raster-brightness-max",
      filter["raster-brightness-max"],
    );
    map.setPaintProperty(
      MAP_BASE_RASTER_LAYER_ID,
      "raster-saturation",
      filter["raster-saturation"],
    );
    map.setPaintProperty(MAP_BASE_RASTER_LAYER_ID, "raster-contrast", filter["raster-contrast"]);
  }
}

/**
 * The basemap kind a :type:`TileSourceSpec` resolves to, or the kind a
 * live map is *currently* rendering. ``"raster"`` is the default for a
 * spec with no explicit ``type`` (legacy descriptors predate the field).
 */
export type BasemapKind = "raster" | "vector";

/**
 * What a live map is currently rendering: a basemap kind, or ``"none"`` —
 * the #1002 initial style (background only, no basemap source) before the
 * first descriptor lands. Distinct from ``null`` (undeterminable — a bare
 * mock without ``getSource``), which must never trigger a build.
 */
export type LiveBasemapKind = BasemapKind | "none";

/**
 * The full set of layer ids any basemap sub-graph can own — the union of
 * the raster set (``background`` + the raster layer) and the vector set
 * (:const:`VECTOR_BASEMAP_LAYER_IDS`, which itself begins with the shared
 * ``background`` id). Used to (a) remove exactly the *old* basemap layers
 * on a rebuild and (b) find the first **overlay** layer (the first live
 * layer whose id is NOT in this set) to insert the new basemap beneath —
 * so the imperatively-added overlay data layers (snapshot / cluster /
 * trail / detection / alert / geometry) stay on top.
 *
 * ``background`` appears in both basemap sets; a ``Set`` dedupes it.
 */
const ALL_BASEMAP_LAYER_IDS: ReadonlySet<string> = new Set<string>([
  MAP_BACKGROUND_LAYER_ID,
  MAP_BASE_RASTER_LAYER_ID,
  ...VECTOR_BASEMAP_LAYER_IDS,
]);

/**
 * The basemap kind the map is **currently rendering**, by probing which
 * basemap *source* is live — the raster source (:const:`MAP_BASE_SOURCE_ID`)
 * ⇒ ``"raster"``, the vector source (:const:`VECTOR_BASEMAP_SOURCE_ID`) ⇒
 * ``"vector"``, neither ⇒ ``"none"`` (the #1002 background-only initial
 * style, waiting for its first descriptor).
 *
 * The source — not a layer — is the discriminator: the two basemap kinds
 * use **distinct** source ids (the layers share only the ``background``
 * id), so a present source id is an unambiguous signal of the rendered
 * kind. No-throws on a removed map (the caller guards with
 * :func:`isMapUsable`); a bare mock without ``getSource`` returns ``null``
 * ("undeterminable"), which — unlike ``"none"`` — never triggers a build.
 *
 * @param map - The live MapLibre map instance.
 * @returns The rendered basemap kind, ``"none"``, or ``null`` when
 *     undeterminable.
 *
 * Cross-references:
 *   - ADR-016 §4 + 2026-06-10 amendment (raster/vector basemap kinds).
 *   - #1002 (no basemap until the descriptor resolves).
 */
export function liveBasemapKind(map: MapLibreMap): LiveBasemapKind | null {
  if (typeof map.getSource !== "function") {
    return null;
  }
  if (map.getSource(VECTOR_BASEMAP_SOURCE_ID) !== undefined) {
    return "vector";
  }
  if (map.getSource(MAP_BASE_SOURCE_ID) !== undefined) {
    return "raster";
  }
  return "none";
}

/**
 * Whether a basemap **source-graph rebuild** is required to render
 * ``spec`` on a map currently rendering ``liveKind`` — i.e. whether the
 * cheap :func:`applyMapTheme` in-place update is *insufficient*.
 *
 * A rebuild is needed when:
 *
 *   1. **The kind changed** (raster ↔ vector). ``applyMapTheme`` can only
 *      ``setTiles`` a raster source or re-paint vector layers in place; it
 *      cannot turn one source kind into the other. This is the headline
 *      bug: the initial fail-safe raster → the late-resolved vector
 *      default, and any Profile picker switch across kinds, were no-ops.
 *   2. **A vector source-URL change** (vector → vector, different
 *      TileJSON ``url``). A MapLibre vector source's ``url`` is fixed at
 *      construction — there is no ``setTiles`` equivalent that re-points a
 *      vector source's TileJSON — so a different vector descriptor needs a
 *      fresh source. (A raster → raster URL change does **not** need a
 *      rebuild: ``applyMapTheme``'s ``setTiles`` fast-path re-points it.)
 *
 * When ``liveKind`` is ``"none"`` (the #1002 background-only initial
 * style — the map has no basemap yet) ANY resolved spec needs a build:
 * this is how the first descriptor reaches a map that mounted before
 * ``/api/config`` was applied. When it is ``null`` (undeterminable — a
 * bare mock without ``getSource``) return ``false`` so nothing is ever
 * built spuriously.
 *
 * @param liveKind - The currently-rendered kind (:func:`liveBasemapKind`).
 * @param liveVectorUrl - The live vector source's TileJSON ``url`` (only
 *     meaningful when ``liveKind === "vector"``; pass ``undefined``
 *     otherwise). Used for the vector-URL-change check.
 * @param spec - The newly-resolved target tile spec.
 * @returns ``true`` when a rebuild is required; ``false`` when
 *     :func:`applyMapTheme` suffices.
 *
 * Cross-references:
 *   - REQ-UI-033 / REQ-UI-034 / REQ-UI-091 (config-/Profile-driven basemap).
 *   - ADR-016 §4 + 2026-06-10 amendment.
 */
export function basemapRebuildNeeded(
  liveKind: LiveBasemapKind | null,
  liveVectorUrl: string | undefined,
  spec: TileSourceSpec,
): boolean {
  if (liveKind === null) {
    return false;
  }
  if (liveKind === "none") {
    return true;
  }
  const targetKind: BasemapKind = spec.type ?? "raster";
  if (targetKind !== liveKind) {
    return true;
  }
  // Same kind. Only the vector→vector source-URL change needs a rebuild
  // (no in-place re-point for a vector source's TileJSON); raster→raster
  // is handled by ``applyMapTheme``'s ``setTiles`` fast-path.
  if (targetKind === "vector") {
    return liveVectorUrl !== spec.urlTemplate;
  }
  return false;
}

/**
 * The live vector source's TileJSON ``url`` (the descriptor
 * ``urlTemplate`` it was built from), or ``undefined`` when the map isn't
 * rendering a vector basemap / the source can't be probed.
 *
 * Read so :func:`basemapRebuildNeeded` can detect a vector → vector
 * descriptor swap (the OpenFreeMap planet TileJSON URL changing) that the
 * in-place theme flip cannot honour. MapLibre's ``VectorTileSource``
 * carries its ``url`` on the source object; a minimal mock may omit it,
 * in which case we return ``undefined`` (treated as "unknown" — a rebuild
 * is then driven only by a kind change, never spuriously).
 *
 * @param map - The live MapLibre map instance.
 * @returns The vector source ``url``, or ``undefined``.
 */
export function liveVectorSourceUrl(map: MapLibreMap): string | undefined {
  if (typeof map.getSource !== "function") {
    return undefined;
  }
  const source = map.getSource(VECTOR_BASEMAP_SOURCE_ID) as { url?: string } | undefined;
  return source?.url;
}

/**
 * Rebuild the basemap **sub-graph** in place to render ``spec`` in
 * ``resolvedTheme`` — the path the cheap :func:`applyMapTheme` cannot
 * take, used when the basemap *kind* changes (raster ↔ vector) or a
 * vector source's TileJSON ``url`` changes (:func:`basemapRebuildNeeded`).
 *
 * # The invariant this preserves
 *
 * Like :func:`applyMapTheme`, this is **never** ``map.setStyle()`` — the
 * imperatively-added overlay data layers (snapshot / cluster / trail /
 * track-tail / detection / alert / geometry) are keyed on the loaded map
 * instance, not the style, so a full ``setStyle`` would wipe them and they
 * would not re-add. Instead this performs a **surgical** swap of only the
 * basemap layers + source:
 *
 *   1. Snapshot the live layer order; find the first **overlay** layer —
 *      the first layer whose id is NOT a basemap id
 *      (:const:`ALL_BASEMAP_LAYER_IDS`). That id is the ``beforeId`` the
 *      new basemap layers insert *beneath*, so the overlays stay on top.
 *   2. Remove every live basemap layer, then both basemap sources (raster
 *      + vector — whichever is present; removing both is safe and covers
 *      the kind we're leaving). Layers are removed before their source
 *      (MapLibre rejects removing a source still referenced by a layer).
 *   3. Build the new basemap from :func:`buildMapStyleFromSpec` (the same
 *      factory the once-at-mount initial style uses, so the rebuilt
 *      basemap is byte-identical to a fresh mount in that theme + spec).
 *      Add its source, then its layers — each inserted at ``beforeId`` so
 *      the whole new basemap lands *below* the first overlay, in the
 *      factory's draw order.
 *
 * No-ops safely on a not-ready / removed map (CLAUDE.md §8.1), on a
 * minimal mock missing the mutation methods, and on a style whose JSON has
 * not been parsed yet (``getStyle()`` is ``undefined`` — every mutator
 * below would throw ``"Style is not done loading."``; see
 * :func:`map-ready.isStyleReady`) — a half-built map simply keeps its
 * current basemap until its surfaces exist. The driving effect in
 * :file:`Map.tsx` defers through :func:`map-ready.runWhenStyleReady`, so
 * the rebuild it declined here runs on ``style.load``.
 *
 * @param map - The loaded MapLibre map instance.
 * @param resolvedTheme - ``"light"`` / ``"dark"`` (caller resolves
 *     ``"system"`` first per REQ-UI-041).
 * @param spec - The target :type:`TileSourceSpec` to render.
 *
 * Cross-references:
 *   - REQ-UI-033 / REQ-UI-034 (browser-direct + retained BFF proxy).
 *   - REQ-UI-040 / REQ-UI-045 (paired themes; theme-correct first paint).
 *   - REQ-UI-046 (overlay layers untouched → status contrast holds).
 *   - REQ-UI-091 (Profile-selected basemap — the picker switch this fixes).
 *   - ADR-016 §4 + 2026-06-10 amendment.
 */
export function rebuildBasemap(
  map: MapLibreMap,
  resolvedTheme: ResolvedTheme,
  spec: TileSourceSpec,
): void {
  if (!isMapUsable(map)) {
    return;
  }
  // A bare mock (or a map mid-construction) missing any mutation method
  // can't be rebuilt — no-op rather than throw (CLAUDE.md §8.1).
  if (
    typeof map.getStyle !== "function" ||
    typeof map.addLayer !== "function" ||
    typeof map.removeLayer !== "function" ||
    typeof map.addSource !== "function" ||
    typeof map.removeSource !== "function" ||
    typeof map.getLayer !== "function"
  ) {
    return;
  }

  // 1) Find the first overlay layer (the insert anchor). Read the live
  //    layer order from the style; the first id that is NOT a basemap id
  //    is the first overlay. ``undefined`` ⇒ no overlays yet (the new
  //    basemap layers then append at the end, which is still correct —
  //    there is nothing above them to sit beneath).
  // ``getStyle()`` is typed ``StyleSpecification`` but ``Style.serialize``
  // returns ``undefined`` until the style JSON has parsed — the window a
  // watchdog-promoted map in a hidden tab sits in (REQ-UI-166; the
  // "reading 'layers'" crash). Nothing below is legal in that window.
  const liveStyle: StyleSpecification | undefined = map.getStyle();
  if (liveStyle === undefined) {
    return;
  }
  const liveLayers = liveStyle.layers ?? [];
  const firstOverlayId = liveLayers.find((l) => !ALL_BASEMAP_LAYER_IDS.has(l.id))?.id;

  // 2) Remove the live basemap layers (in any order — they don't depend on
  //    each other), then both basemap sources. Guard every removal on
  //    presence so leaving raster→vector (or vector→raster) only removes
  //    what's actually there.
  for (const id of ALL_BASEMAP_LAYER_IDS) {
    if (map.getLayer(id) !== undefined) {
      map.removeLayer(id);
    }
  }
  for (const sourceId of [MAP_BASE_SOURCE_ID, VECTOR_BASEMAP_SOURCE_ID]) {
    if (map.getSource(sourceId) !== undefined) {
      map.removeSource(sourceId);
    }
  }

  // 3) Build the new basemap from the SAME factory the initial style uses,
  //    so a rebuilt basemap is identical to a fresh mount in this theme +
  //    spec. Add the source, then each layer at ``beforeId`` so the whole
  //    new basemap lands below the first overlay, in factory draw order.
  const next = buildMapStyleFromSpec(resolvedTheme, spec);
  for (const [sourceId, source] of Object.entries(next.sources)) {
    map.addSource(sourceId, source);
  }
  for (const layer of next.layers as LayerSpecification[]) {
    // ``addLayer(layer, beforeId)`` — ``undefined`` beforeId appends on
    // top (only reached when there are no overlays yet).
    map.addLayer(layer, firstOverlayId);
  }
}
